import {
	type ExperienceResponse,
	type MeResponse,
	matriculaCaducada,
	type PuertaCreacionExperiencia,
	type ResultadoEnlaceOrganizador,
	type RolAccessMatrix,
	resolverPuertaCreacion,
} from '@diario/common';
import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '../components/ui/alert';
import { sesionQueryOptions } from '../features/auth/api/useSesion';
import { usePublicarExperiencia } from '../features/experiences/api/usePublicarExperiencia';
import {
	ArranqueRapido,
	type DatosPlantillaElegida,
} from '../features/experiences/components/arranqueRapido/arranqueRapido';
import {
	AsistenteCrearExperiencia,
	type ValoresInicialesAsistente,
} from '../features/experiences/components/asistente/asistenteCrearExperiencia';
import { PantallaExito } from '../features/experiences/components/exito/pantallaExito';
import { PuertaCreacion } from '../features/experiences/components/puertas/puertaCreacion';
import { comprobarEnlaceQueryOptions } from '../features/invitations/api/useComprobarEnlace';
import { SendaRoles } from '../features/roles/components/sendaRoles';

/** Lo mismo que `login.tsx`: dos cadenas opcionales leídas de `location.search`, sin
 * estructura ni validación de contrato — el token viaja tal cual y se comprueba (nunca se
 * consume) con `useComprobarEnlace`/`comprobarEnlaceQueryOptions`. Aceptar solo un valor de
 * tipo cadena, y descartar cualquier otra cosa (array, objeto, número), es la misma disciplina
 * que `leerBusquedaLogin` aplica a `redirect`: la ruta solo confía en valores internos que ella
 * misma sabe interpretar, nunca en lo que llegue crudo en la URL. */
interface BusquedaCrearExperiencia {
	/** Token del enlace de organizador (documento 04 §6, `?inv=`). */
	inv?: string;
}

const leerBusquedaCrearExperiencia = (busqueda: Record<string, unknown>): BusquedaCrearExperiencia => ({
	inv: typeof busqueda.inv === 'string' ? busqueda.inv : undefined,
});

/** Lo que `beforeLoad` deja en el contexto de la ruta para que el componente no repita ninguna
 * petición: la sesión (ausente si no hay ninguna — esta ruta ya no la exige para verse, oleada
 * 6), el resultado de comprobar el enlace si llegó uno y se pudo comprobar, la puerta ya
 * resuelta, el token — solo cuando el enlace es válido — listo para pasar al asistente, y el
 * grupo de WhatsApp del enlace, si trajo uno (documento 02c, decisión 5). */
interface ContextoCrearExperiencia {
	puerta: PuertaCreacionExperiencia;
	resultadoEnlace?: ResultadoEnlaceOrganizador;
	sesion?: MeResponse;
	tokenEnlace?: string;
	whatsappGroupUrlInicial?: string;
}

/**
 * `RolAccessMatrix` (`common/rules/accessMatrix.ts`) no tiene parada propia para
 * `systemRole = 'ADMIN'`. Decisión propia de esta ruta, coherente con cómo el resto del motor
 * de reglas trata el caso: un administrador supera siempre el control de acceso de creación
 * oficial (`resolverPuertaCreacion`, `puedeCrearConEtiqueta`), igual que un colaborador —
 * mapearlo a `'colaborador'` es lo único que mantiene visible el interruptor "¿crear plan
 * oficial?" del asistente para quien el servidor SÍ va a dejar crear una oficial.
 *
 * Documento 02c: `LEGACY` ya tiene parada propia (`'legacy'`, copia exacta de `'standard'` en
 * las trece capacidades) — deja de mapearse a `'standard'`. Sin sesión, la parada es
 * `'visitante'`, la primera de las ocho: esta ruta ya se ve sin sesión (oleada 6) y el
 * asistente solo se monta cuando la puerta es `'formulario'`, así que en la práctica nunca
 * llega aquí sin sesión — pero la función se mantiene total (nunca lanza) para no acoplar su
 * contrato a esa garantía externa.
 */
const rolAccessMatrixDesdeSesion = (sesion: MeResponse | undefined): RolAccessMatrix => {
	if (sesion === undefined) {
		return 'visitante';
	}
	if (sesion.systemRole === 'ADMIN') {
		return 'colaborador';
	}
	if (sesion.tier === 'PENDING') {
		return 'pendiente';
	}
	if (sesion.tier === 'GRUMETE') {
		return 'grumete';
	}
	if (sesion.tier === 'LITE') {
		return 'lite';
	}
	if (sesion.tier === 'COLLABORATOR') {
		return 'colaborador';
	}
	if (sesion.tier === 'LEGACY') {
		return 'legacy';
	}
	return 'standard';
};

/** `Date | null` a partir de `MeResponse.membershipValidUntil` (`string | undefined`, nunca
 * `null` en este DTO): sin matrícula comprada nunca, el campo no viaja — se traduce a `null`,
 * que es justo lo que `matriculaCaducada` (`@diario/common/rules/experiences`) espera para
 * "sin matrícula", distinto de "caducada". */
const validezMatriculaDesdeSesion = (sesion: MeResponse): Date | null =>
	sesion.membershipValidUntil === undefined ? null : new Date(sesion.membershipValidUntil);

/**
 * P-09: el número de soporte real (WhatsApp del equipo, no el de las automatizaciones de
 * ManyChat — 657 470 611 — mandar ahí a alguien con un problema real rompería la separación
 * entre el canal que atiende una persona y el canal del bot). Dato de configuración, no texto
 * traducible: no vive en i18n, a propósito. Formato exigido por `wa.me`: sin `+` ni espacios.
 */
const NUMERO_WHATSAPP_SOPORTE = '34623046262';

/** El enlace `wa.me` al soporte real, con el mensaje ya escrito (`experiences:puertas.*.mensajeWhatsapp`) — mismo molde `https://wa.me/<numero>?text=<mensaje>` que ya usa `experiences.mapper.ts` en el backend para los enlaces de grupo, aquí con el número fijo de soporte en vez de uno de experiencia. */
const enlaceWhatsappSoporte = (mensaje: string): string =>
	`https://wa.me/${NUMERO_WHATSAPP_SOPORTE}?text=${encodeURIComponent(mensaje)}`;

/**
 * `/crear-experiencia` (documento 03a §7.1; documento 03b §7.1/§10.3; documento 02c). Fuera
 * del layout autenticado desde la oleada 6: la dirección es pública y enseña la puerta que
 * corresponda — quien no tiene sesión ve `PuertaSinSesion`, nunca rebota a `/login` antes de
 * pintar nada. Ninguna puerta puede filtrar datos de "experience": la resolución de qué puerta
 * mostrar no depende de nada que viva al otro lado, así que enseñar la puerta nunca entreabre
 * la comunidad (regla del cliente, oleada 6).
 *
 * `beforeLoad` intenta la sesión con `ensureQueryData` y, si falla (no hay sesión, o no se
 * pudo comprobar — mismo criterio "ante la duda, no se entra" que `_autenticado.tsx`, pero
 * aquí sin redirigir: esta ruta se queda abierta y dejar `sesion` en `undefined` ya basta para
 * que `resolverPuertaCreacion` devuelva `'sinSesion'`), sigue sin lanzar nada. Solo se comprueba
 * el enlace de organizador (`comprobarEnlaceQueryOptions`) cuando SÍ hay sesión: sin sesión la
 * puerta siempre es `'sinSesion'` (es la primera comprobación de `resolverPuertaCreacion`), así
 * que el enlace de la URL no se pierde — viaja en `?inv=` y se revisa de nuevo al volver del
 * acceso, con la sesión ya creada.
 */
export const Route = createFileRoute('/crear-experiencia')({
	beforeLoad: async ({ context, search }): Promise<ContextoCrearExperiencia> => {
		let sesion: MeResponse | undefined;
		try {
			sesion = await context.queryClient.ensureQueryData(sesionQueryOptions);
		} catch {
			sesion = undefined;
		}

		const token = search.inv;
		let resultadoEnlace: ResultadoEnlaceOrganizador | undefined;
		let whatsappGroupUrlInicial: string | undefined;
		if (sesion !== undefined && token !== undefined && token.length > 0) {
			try {
				const comprobacion = await context.queryClient.ensureQueryData(comprobarEnlaceQueryOptions(token));
				resultadoEnlace = comprobacion.resultado;
				// Documento 02c, decisión 5: el enlace puede traer ya el grupo de WhatsApp del plan —
				// llega resuelto al paso 6 del asistente vía `whatsappGroupUrlInicial`.
				if (comprobacion.resultado === 'ok' && comprobacion.whatsappGroupUrl) {
					whatsappGroupUrlInicial = comprobacion.whatsappGroupUrl;
				}
			} catch {
				// Ante la duda, el enlace no sirve: un enlace que no se pudo verificar nunca abre la
				// comunidad (mismo espíritu que CA-03a.7 con los otros tres estados rotos).
				resultadoEnlace = 'not-found';
			}
		}

		const puerta = resolverPuertaCreacion({
			haySesion: sesion !== undefined,
			// Sin matrícula comprada nunca ("sin sesión" incluido, donde el dato ni siquiera existe)
			// no es lo mismo que caducada — ver `validezMatriculaDesdeSesion`.
			matriculaCaducada:
				sesion === undefined ? false : matriculaCaducada(validezMatriculaDesdeSesion(sesion), new Date()),
			resultadoEnlace,
			// Sin sesión, `resolverPuertaCreacion` devuelve `'sinSesion'` antes de mirar `systemRole`/
			// `tier` (es la primera comprobación del árbol) — estos dos valores son placeholders
			// inertes en ese caso, nunca se leen.
			systemRole: sesion?.systemRole ?? 'USER',
			tier: sesion?.tier ?? 'PENDING',
		});

		return {
			puerta,
			resultadoEnlace,
			sesion,
			tokenEnlace: resultadoEnlace === 'ok' ? token : undefined,
			whatsappGroupUrlInicial,
		};
	},
	component: PantallaCrearExperiencia,
	validateSearch: leerBusquedaCrearExperiencia,
});

type FaseFormulario = 'arranqueRapido' | 'asistente';

function PantallaCrearExperiencia(): React.JSX.Element {
	const navigate = useNavigate();
	// Destino a guardar en `?redirect=` antes de mandar al acceso (pathname + query + hash, mismo
	// criterio que `_autenticado.tsx`): así, al volver de `/login`, se entra directo al asistente
	// (con el `?inv=` que trajera incluido) en vez de a la portada.
	const destino = useLocation({ select: location => location.href });
	const { puerta, resultadoEnlace, sesion, tokenEnlace, whatsappGroupUrlInicial } = Route.useRouteContext();
	const { t } = useTranslation('experiences');

	const [mostrarSendaRoles, setMostrarSendaRoles] = useState(false);
	// P-09: "Renovar" (matrícula caducada, salida principal) no navega a ningún sitio — la pantalla
	// de membresía llega en una entrega próxima (documento 07a) — se queda en la misma puerta y
	// enseña el aviso honesto (`puertas.matriculaCaducada.avisoProximamente`) en vez de fingir un
	// destino que todavía no existe.
	const [mostrarAvisoRenovacion, setMostrarAvisoRenovacion] = useState(false);
	const [fase, setFase] = useState<FaseFormulario>('arranqueRapido');
	const [valoresIniciales, setValoresIniciales] = useState<ValoresInicialesAsistente | undefined>(undefined);
	const [experienciaCreada, setExperienciaCreada] = useState<ExperienceResponse | undefined>(undefined);

	const publicar = usePublicarExperiencia();

	const manejarSalidaPuerta = (puertaActual: PuertaCreacionExperiencia, salida: 'principal' | 'secundaria'): void => {
		if (puertaActual === 'enlaceRoto' && salida === 'secundaria') {
			// T-2.8: "Monto uno para mis amigos" nunca bloquea (RN-11) — vuelve a esta misma ruta sin
			// el token roto y deja que `resolverPuertaCreacion` reevalúe con el rol de quien pregunta
			// (el formulario directamente, o su propia puerta de rol si detrás del enlace también
			// había un bloqueo por tier).
			void navigate({ search: {}, to: '/crear-experiencia' });
			return;
		}
		if (
			(puertaActual === 'sinSesion' || puertaActual === 'pendiente' || puertaActual === 'grumete')
			&& salida === 'secundaria'
		) {
			// "Ver los roles" / "¿Qué es la misión 2?" / "¿Qué es la misión 3?": la senda de roles ya
			// existe (`features/roles`, usada hoy bajo `/login`) y no depende de sesión — se revela
			// aquí en vez de navegar a una pantalla de "misiones" que esta iteración no construye.
			setMostrarSendaRoles(true);
			return;
		}
		if (puertaActual === 'sinSesion' && salida === 'principal') {
			// Oleada 6: guarda a dónde iba (`?redirect=`) para que, al volver con sesión creada,
			// `/login` navegue directo aquí (con el `?inv=` que trajera) en vez de a la portada —
			// mismo mecanismo que `_autenticado.tsx`, `safeRedirect` lo revalida del otro lado.
			void navigate({ search: { redirect: destino }, to: '/login' });
			return;
		}
		if (puertaActual === 'pendiente' && salida === 'principal') {
			// P-09 (respuestas del cliente 2026-09-17): "Ver experiencias" — el catálogo ya no exige
			// sesión (oleada «rescate navegación» 2026-09-17), y `soloOficiales` es el único campo NO
			// opcional de `BusquedaExperiencias` (`routes/experiencias.tsx`), así que hace falta un
			// valor explícito. Mismo criterio que las dos "ver experiencias OFICIALES" de
			// `routes/unirse.$id.tsx` (`manejarSalidaRedirect`, `invitationRequired`/principal y
			// `membershipGate`/secundaria): quien todavía no puede montar un plan tiene que ir primero
			// a una experiencia oficial (la única vía que cuenta para desbloquear esta puerta, RN-11),
			// así que el catálogo se filtra de entrada a oficiales en vez de enseñar el calendario entero.
			void navigate({ search: { soloOficiales: true }, to: '/experiencias' });
			return;
		}
		if (puertaActual === 'grumete' && salida === 'principal') {
			// P-09 (respuestas del cliente 2026-09-17): "Ver el calendario oficial" habla del
			// CALENDARIO, no de un listado acotado — mismo criterio que "Ver el calendario" de
			// `routes/unirse.$id.tsx` (`recommendations`/principal): el catálogo entero, sin
			// `soloOficiales`, porque es la vista de calendario completa la que promete el propio texto.
			void navigate({ search: { soloOficiales: false }, to: '/experiencias' });
			return;
		}
		if (puertaActual === 'enlaceRoto' && salida === 'principal') {
			// P-09: "Pedir otro" — no hay pantalla propia para reemitir un enlace de organizador en
			// este repositorio, así que se manda al soporte real por WhatsApp (nunca al número de
			// ManyChat, ver `NUMERO_WHATSAPP_SOPORTE`) con el mensaje ya escrito.
			window.open(enlaceWhatsappSoporte(t('puertas.enlaceRoto.mensajeWhatsapp')), '_blank', 'noopener,noreferrer');
			return;
		}
		if (puertaActual === 'matriculaCaducada' && salida === 'secundaria') {
			// P-09: "Hablar con una persona" — mismo soporte real por WhatsApp que "Pedir otro".
			window.open(
				enlaceWhatsappSoporte(t('puertas.matriculaCaducada.mensajeWhatsapp')),
				'_blank',
				'noopener,noreferrer',
			);
			return;
		}
		if (puertaActual === 'matriculaCaducada' && salida === 'principal') {
			// P-09: "Renovar" se queda en la misma puerta — no hay ningún sitio honesto al que
			// llevarla todavía (ver el estado `mostrarAvisoRenovacion`, arriba).
			setMostrarAvisoRenovacion(true);
			return;
		}
		// Defensivo: `PuertaCreacionExperiencia` incluye `'formulario'`, que nunca llega aquí (esta
		// función solo se invoca desde la rama `puerta !== 'formulario'`, más abajo) — las cinco
		// puertas reales ya están todas cubiertas arriba. Se mantiene por si `resolverPuertaCreacion`
		// ganara una puerta nueva sin que este `switch` a mano se actualizara a la vez.
		void navigate({ to: '/' });
	};

	if (puerta !== 'formulario') {
		return (
			<div className='flex flex-col gap-6'>
				<PuertaCreacion
					onSalidaPrincipal={() => manejarSalidaPuerta(puerta, 'principal')}
					onSalidaSecundaria={() => manejarSalidaPuerta(puerta, 'secundaria')}
					puerta={puerta}
					resultadoEnlace={resultadoEnlace}
				/>
				{mostrarAvisoRenovacion ? (
					<div className='mx-auto w-full max-w-md'>
						<Alert variante='aviso'>{t('puertas.matriculaCaducada.avisoProximamente')}</Alert>
					</div>
				) : null}
				{mostrarSendaRoles ? (
					<div className='mx-auto w-full max-w-2xl'>
						<SendaRoles />
					</div>
				) : null}
			</div>
		);
	}

	if (experienciaCreada !== undefined) {
		return (
			<PantallaExito
				esColaborador={sesion?.tier === 'COLLABORATOR'}
				experiencia={experienciaCreada}
				onIrATablon={() => void navigate({ params: { id: experienciaCreada.id }, to: '/experiencia/$id/tablon' })}
			/>
		);
	}

	const manejarExperienciaCreada = (experiencia: ExperienceResponse): void => {
		// Decisión 2 (`decisiones.md`): nace en `DRAFT`, publicar es un segundo paso explícito, que
		// dispara esta ruta — no el asistente ni el servicio de creación. Se dispara sin esperarla
		// (PantallaExito no tiene estado de carga propio, especificacion.md § Vistas: se muestra
		// "sobre datos ya recibidos en la respuesta de creación").
		publicar.mutate(experiencia.id);
		// El borrador lo descarta el asistente dentro de su propio `enviar()`, no esta ruta: es
		// quien lo crea, quien lo guarda paso a paso y quien lo descarta al empezar de cero, así
		// que el ciclo de vida entero vive en un solo sitio. Durante un tiempo lo descartaron los
		// dos y salían dos peticiones de borrado sobre el mismo borrador, la segunda contra algo
		// que ya no existía.
		setExperienciaCreada(experiencia);
	};

	if (fase === 'arranqueRapido') {
		return (
			<ArranqueRapido
				onElegirPlantilla={(datos: DatosPlantillaElegida) => {
					// Documento 03c, decisión 4: los cuatro campos deducibles se prefijan tal cual en el
					// asistente — coinciden campo a campo con `ValoresInicialesAsistente`. `datos.pasos`
					// (qué pasos quedan ya completos) viaja igual, sin recalcularlo aquí: es el asistente
					// quien decide con ello en qué paso arranca y qué pasos ofrece ya marcados.
					setValoresIniciales({
						categoryId: datos.categoryId,
						durationType: datos.durationType,
						format: datos.format,
						nicheIds: datos.nicheIds,
						pasos: datos.pasos,
					});
					setFase('asistente');
				}}
				onEmpezarDeCero={() => {
					setValoresIniciales(undefined);
					setFase('asistente');
				}}
			/>
		);
	}

	return (
		<AsistenteCrearExperiencia
			onExperienciaCreada={manejarExperienciaCreada}
			rol={rolAccessMatrixDesdeSesion(sesion)}
			tokenEnlace={tokenEnlace}
			valoresIniciales={valoresIniciales}
			whatsappGroupUrlInicial={whatsappGroupUrlInicial}
		/>
	);
}
